import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { examsService } from '@/services/exams.service';
import { Plus, Search, Edit, Trash2, BookOpen, Download } from 'lucide-react';
import { ViewToggle } from '@/components/ViewToggle';
import { PaginationControls } from '@/components/PaginationControls';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Tooltip } from '@/components/Tooltip';
import { StatusBadge } from '@/components/Badge';
import { SortableTable, Column } from '@/components/SortableTable';
import { exportToCSV } from '@/utils/exportCSV';
import { storage } from '@/utils/localStorage';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';
import type { Exam } from '@/types';

export function ExamsList() {
  const queryClient = useQueryClient();

  // Load smart defaults
  const savedStatusFilter = storage.getFormValue<string>('examsList', 'statusFilter') || 'active';
  const savedView = storage.getPreferences().defaultView || 'list';

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(savedStatusFilter);
  const [view, setView] = useState<'grid' | 'list'>(savedView);
  const [paginationMode, setPaginationMode] = useState<'pagination' | 'infinite'>('pagination');
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCount, setDisplayCount] = useState(15);
  const itemsPerPage = 15;

  // Save preferences to localStorage
  useEffect(() => {
    storage.rememberFormValue('examsList', 'statusFilter', statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    storage.setPreferences({ defaultView: view });
  }, [view]);

  const deleteMutation = useMutation({
    mutationFn: async (examId: string) => {
      return await examsService.deleteExam(examId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      showDeleteSuccess('Exam has been deleted successfully.');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to delete exam';
      showDeleteError(message);
    },
  });

  const handleDeleteExam = async (examId: string, examName: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Exam?',
      itemName: examName,
    });

    if (confirmed) {
      deleteMutation.mutate(examId);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['exams', { search, status: statusFilter }],
    queryFn: async () => {
      try {
        return await examsService.getExams({
          search,
          status: statusFilter === 'all' ? undefined : statusFilter
        });
      } catch (error) {
        console.error('Failed to fetch exams:', error);
        return [];
      }
    },
  });

  const allExams = data || [];
  const totalItems = allExams.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Display exams based on mode
  const exams = useMemo(() => {
    if (paginationMode === 'infinite') {
      return allExams.slice(0, displayCount);
    } else {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return allExams.slice(startIndex, endIndex);
    }
  }, [allExams, paginationMode, displayCount, currentPage, itemsPerPage]);

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + itemsPerPage);
  };

  const handlePaginationModeChange = (mode: 'pagination' | 'infinite') => {
    setPaginationMode(mode);
    if (mode === 'pagination') {
      setCurrentPage(1);
    } else {
      setDisplayCount(itemsPerPage);
    }
  };

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
    setDisplayCount(itemsPerPage);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
    setDisplayCount(itemsPerPage);
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setCurrentPage(1);
    setDisplayCount(itemsPerPage);
  };

  const handleExportCSV = () => {
    exportToCSV(
      allExams,
      'exams',
      [
        { key: 'exam_id', label: 'Exam ID' },
        { key: 'name', label: 'Name' },
        { key: 'certifying_body', label: 'Certifying Body' },
        { key: 'exam_status', label: 'Status' },
        { key: 'total_questions', label: 'Total Questions' },
        { key: 'duration_minutes', label: 'Duration (minutes)' },
        { key: 'question_count', label: 'Questions in Bank' },
      ]
    );
  };

  // Define columns for SortableTable
  const columns: Column<Exam>[] = [
    {
      key: 'exam_id',
      label: 'Exam ID',
      sortable: true,
      render: (val) => <span className="font-mono text-sm font-medium text-gray-900">{String(val)}</span>,
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_, exam) => (
        <div>
          <p className="font-medium text-gray-900">{exam.name}</p>
          <div className="mt-1 text-xs text-gray-500">
            {exam.total_questions} questions • {Math.floor(exam.duration_minutes / 60)}h{' '}
            {exam.duration_minutes % 60}m
          </div>
          {exam.question_count && (
            <div className="mt-1 text-xs text-primary-600">
              {exam.question_count.toLocaleString()} questions in bank
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'certifying_body',
      label: 'Body',
      sortable: true,
      render: (val) => <span className="text-sm text-gray-600">{String(val)}</span>,
    },
    {
      key: 'exam_status',
      label: 'Status',
      sortable: true,
      render: (val) => <StatusBadge status={String(val)} />,
    },
    {
      key: 'total_questions',
      label: 'Questions',
      sortable: true,
      render: (val) => <span className="text-sm text-gray-600">{val}</span>,
    },
    {
      key: 'exam_id',
      label: 'Actions',
      sortable: false,
      render: (_, exam) => (
        <div className="flex items-center space-x-2">
          <Tooltip content="Edit exam details">
            <Link
              to={`/exams/${exam.exam_id}/edit`}
              className="p-2 text-gray-600 transition-colors hover:text-primary-600 hover:bg-gray-100 rounded"
            >
              <Edit className="w-4 h-4" />
            </Link>
          </Tooltip>
          {/* <Tooltip content="View exam blueprint">
            <Link
              to={`/exams/${exam.exam_id}/blueprint`}
              className="p-2 text-gray-600 transition-colors hover:text-blue-600 hover:bg-gray-100 rounded"
            >
              <FileText className="w-4 h-4" />
            </Link>
          </Tooltip> */}
          <Tooltip content="Delete exam">
            <button
              onClick={() => handleDeleteExam(exam.exam_id, exam.name)}
              className="p-2 text-gray-600 transition-colors hover:text-red-600 hover:bg-gray-100 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header with Breadcrumbs */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Exams"
          description="Manage certification exams and their blueprints"
          breadcrumbs={[{ label: 'Content Management', path: '#' }, { label: 'Exams' }]}
        />
        <div className="flex items-center space-x-3 mt-10">
          <Tooltip content="Export all exams to CSV file">
            <button
              onClick={handleExportCSV}
              disabled={allExams.length === 0}
              className="btn-secondary flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </Tooltip>
          <Tooltip content="Create a new exam">
            <Link to="/exams/create" className="btn-primary flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              Create Exam
            </Link>
          </Tooltip>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Tooltip content="Total number of exams">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Exams</p>
                <p className="text-3xl font-bold text-gray-900">{allExams.length}</p>
              </div>
              <BookOpen className="w-10 h-10 text-blue-500" />
            </div>
          </div>
        </Tooltip>
        <Tooltip content="Currently active exams">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-3xl font-bold text-gray-900">
                  {allExams.filter(e => e.exam_status === 'active').length}
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-full">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </Tooltip>
        <Tooltip content="Inactive or paused exams">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Inactive</p>
                <p className="text-3xl font-bold text-gray-900">
                  {allExams.filter(e => e.exam_status === 'inactive').length}
                </p>
              </div>
              <div className="p-2 bg-red-100 rounded-full">
                <BookOpen className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </Tooltip>
        <Tooltip content="Legacy or deprecated exams">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Legacy</p>
                <p className="text-3xl font-bold text-gray-900">
                  {allExams.filter(e => e.exam_status === 'legacy').length}
                </p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-full">
                <BookOpen className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </Tooltip>
        <Tooltip content="Upcoming or planned exams">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Upcoming</p>
                <p className="text-3xl font-bold text-gray-900">
                  {allExams.filter(e => e.exam_status === 'upcoming').length}
                </p>
              </div>
              <div className="p-2 bg-blue-100 rounded-full">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </Tooltip>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex items-center space-x-4 flex-1">
            <Tooltip content="Search exams by name or ID">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute w-5 h-5 text-gray-400 left-3 top-2.5" />
                  <input
                    type="text"
                    placeholder="Search exams..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 input-field"
                  />
                </div>
              </div>
            </Tooltip>
            <Tooltip content="Filter exams by status">
              <select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="input-field w-48"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="legacy">Legacy</option>
                <option value="upcoming">Upcoming</option>
              </select>
            </Tooltip>
            {(search || statusFilter !== 'all') && (
              <Tooltip content="Clear all filters">
                <button onClick={handleResetFilters} className="btn-secondary whitespace-nowrap">
                  Reset Filters
                </button>
              </Tooltip>
            )}
          </div>
          <ViewToggle view={view} onViewChange={setView} />
        </div>
      </div>

      {/* Exams Content */}
      <div className="card">
        {isLoading ? (
          <div className="py-12 text-center text-gray-500">Loading...</div>
        ) : exams.length === 0 ? (
          allExams.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No exams yet"
              description="Get started by creating your first exam. Define exam details, set up blueprints, and manage questions."
              action={{
                label: 'Create Your First Exam',
                to: '/exams/create'
              }}
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No exams match your filters"
              description={`Try adjusting your search or filters. ${allExams.length} total exams available.`}
              action={{
                label: 'Reset Filters',
                onClick: handleResetFilters
              }}
              secondaryAction={{
                label: 'View All Exams',
                onClick: () => {
                  setSearch('');
                  setStatusFilter('all');
                }
              }}
            />
          )
        ) : view === 'list' ? (
          <SortableTable
            columns={columns}
            data={exams}
            keyExtractor={(exam) => exam.exam_id}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {exams.map((exam) => (
              <div
                key={exam.exam_id}
                className="card hover:shadow-lg transition-shadow border-l-4 border-primary-500"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="p-3 bg-primary-50 rounded-lg">
                      <BookOpen className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {exam.name}
                      </h3>
                      <p className="text-xs text-gray-500 font-mono mt-1">{exam.exam_id}</p>
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mb-3">
                  <StatusBadge status={exam.exam_status} />
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Body:</span>
                    <span className="font-medium text-gray-900">{exam.certifying_body}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-medium text-gray-900">
                      {Math.floor(exam.duration_minutes / 60)}h {exam.duration_minutes % 60}m
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Questions:</span>
                    <span className="font-medium text-gray-900">{exam.total_questions}</span>
                  </div>
                  {exam.question_count && exam.question_count > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">In Bank:</span>
                      <span className="font-medium text-primary-600">
                        {exam.question_count.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Tooltip content="Edit exam details">
                      <Link
                        to={`/exams/${exam.exam_id}/edit`}
                        className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Link>
                    </Tooltip>
                    {/* <Tooltip content="View exam blueprint">
                      <Link
                        to={`/exams/${exam.exam_id}/blueprint`}
                        className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Blueprint
                      </Link>
                    </Tooltip> */}
                    <Tooltip content="Delete this exam">
                      <button
                        onClick={() => handleDeleteExam(exam.exam_id, exam.name)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalItems > 0 && (
        <PaginationControls
          mode={paginationMode}
          onModeChange={handlePaginationModeChange}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onLoadMore={handleLoadMore}
          hasMore={displayCount < totalItems}
          isLoading={isLoading}
        />
      )}

    </div>
  );
}
